import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { SelectedAddresses } from '~app/modules/checkout/models';
import { AccountInfoService } from '~core/modules/identity/services/account-info.service';
import { select, Store } from '@ngrx/store';
import { addressesSelectors, countriesSelectors } from '~app/modules/checkout/store';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { CheckoutCommonUtil } from '~app/modules/checkout/utils/checkout-common-util';
import { State } from '~app/modules/checkout/store/countries';
import { FeatureToggleEnum } from '~shared/constants';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';

@Injectable()
export class CheckoutAddressService {
  constructor(
    private store$: Store<State>,
    private accountInfoService: AccountInfoService,
    private featureFlagService: ActiveFeaturesConfigcatService
  ) {}

  public getSelectedAddresses(): Observable<SelectedAddresses> {
    const addressesLoading$ = combineLatest([
      this.store$.pipe(select(addressesSelectors.getLoading)),
      this.store$.pipe(select(countriesSelectors.getLoading)),
    ]).pipe(map((isLoadings: boolean[]): boolean => isLoadings.every((isLoading: boolean): boolean => !isLoading)));

    const isEncodingAndDecodingEnabled$: Observable<boolean> = this.featureFlagService.isFeatureEnabledAndReadyV2(
      FeatureToggleEnum.FF_SNL_ENCODING_DECODING_LOCAL_ADDRESS
    );
    const defaults$ = combineLatest([
      this.store$.pipe(select(addressesSelectors.getSelectedOrDefaultBillingAddress)),
      this.store$.pipe(select(addressesSelectors.getSelectedOrDefaultShippingAddress)),
      this.accountInfoService.isAccountEligibleAddressActions(),
      this.store$.pipe(select(addressesSelectors.getFreightForwarding)),
      isEncodingAndDecodingEnabled$,
    ]);

    return addressesLoading$.pipe(
      filter((isLoadingFinished: boolean): boolean => isLoadingFinished),
      switchMap(() => defaults$),
      take(1),
      map(
        ([
          currentBillingAddress,
          currentShippingAddress,
          isAccountEligibleAddressActions,
          freightForwardingPhone,
          isEncodingAndDecodingEnabled,
        ]) => {
          const savedTmpAddress = CheckoutCommonUtil.getTmpAddressesFromLocalStorage(isEncodingAndDecodingEnabled);
          const useAddressesFromLocalStorage =
            !isAccountEligibleAddressActions && savedTmpAddress?.shippingAddress && savedTmpAddress?.billingAddress;

          return {
            shippingAddress: useAddressesFromLocalStorage ? savedTmpAddress.shippingAddress : currentShippingAddress,
            billingAddress: useAddressesFromLocalStorage ? savedTmpAddress.billingAddress : currentBillingAddress,
            freightForwardingPhone,
          };
        }
      )
    );
  }
}
